import type { WalkthroughDefinition } from "~/walkthroughs/types";

import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Drawer,
	Icon,
	IconButton,
	Link,
	ListItemButton,
	Stack,
	Tooltip
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import RetryableErrorState from "~/components/RetryableErrorState";
import { BodyText, BodyTextSmall, CaptionText, TitleText } from "~/components/Typographies";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import canOpenTenantSettings from "~/helpers/canOpenTenantSettings";
import learningPaths, { type LearningConcept } from "~/helpers/learningPaths";
import useLearningMutations from "~/hooks/mutations/useLearningMutations";
import useCanManageTenantSettings from "~/hooks/useCanManageTenantSettings";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useLearnParam from "~/hooks/useLearnParam";
import useStartTour from "~/hooks/useStartTour";
import { useMyLearningPathQuery } from "~/services/endpoints/apiEndpoints";
import { matchesWalkthroughRoute } from "~/walkthroughs/registry";

const PANEL_WIDTH = 420;

interface PendingTour {
	conceptId: string;
	fromPathname: string;
	routePath: string;
	tourId: string;
}

interface LearningConceptRowProps {
	canManageSettings: boolean;
	concept: LearningConcept;
	onSelect: () => void;
	onShow: (tour: WalkthroughDefinition) => void;
	selected: boolean;
}

const LearningConceptRow = ({ canManageSettings, concept, onSelect, onShow, selected }: LearningConceptRowProps) => {
	const { t } = useTranslation("common");
	const { recordConceptExplained } = useLearningMutations();
	const launchableTour = learningPaths.launchableTour(concept.walkthroughId);
	const tour =
		launchableTour && canOpenTenantSettings(launchableTour.routePath, canManageSettings) ? launchableTour : undefined;
	const summary = t(concept.summaryKey, { defaultValue: "" });
	const hasMilestone = Boolean(concept.milestone);
	const isDone = hasMilestone ? Boolean(concept.progress.milestoneObservedAt) : concept.isComplete;

	return (
		<Box sx={{ border: "1px solid", borderColor: selected ? "primary.main" : "divider", borderRadius: 2 }}>
			<ListItemButton aria-expanded={selected} onClick={onSelect} sx={{ borderRadius: 2, gap: 1.5 }}>
				<Icon sx={{ color: concept.isComplete ? "success.main" : "text.disabled" }}>
					{concept.isComplete ? "check_circle" : "radio_button_unchecked"}
				</Icon>
				<BodyText sx={{ flex: 1, fontWeight: 600 }}>
					{t(concept.titleKey, { defaultValue: concept.conceptId })}
				</BodyText>
			</ListItemButton>
			{selected && (
				<Stack spacing={1.5} sx={{ pb: 2, px: 2 }}>
					{summary && <BodyTextSmall sx={{ color: "text.secondary" }}>{summary}</BodyTextSmall>}
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						<Button
							component="a"
							href={learningPaths.docsUrl(concept.docsPath)}
							onClick={() => void recordConceptExplained(concept.conceptId)}
							rel="noopener noreferrer"
							size="small"
							startIcon={<Icon>menu_book</Icon>}
							target="_blank"
							variant="outlined"
						>
							{t("learningPath.explain")}
						</Button>
						{tour && (
							<Button onClick={() => onShow(tour)} size="small" startIcon={<Icon>play_circle</Icon>} variant="outlined">
								{t("learningPath.show")}
							</Button>
						)}
					</Box>
					<Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1 }}>
						<CaptionText bold>{t("learningPath.do")}</CaptionText>
						{isDone ? (
							<Chip color="success" icon={<Icon>check</Icon>} label={t("learningPath.done")} size="small" />
						) : (
							<>
								<CaptionText sx={{ color: "text.secondary" }}>
									{t(hasMilestone ? "learningPath.doHint" : tour ? "learningPath.tourHint" : "learningPath.checkHint")}
								</CaptionText>
								{tour && (
									<Link component={RouterLink} to={tour.routePath} variant="caption">
										{t("learningPath.openPage")}
									</Link>
								)}
							</>
						)}
					</Box>
				</Stack>
			)}
		</Box>
	);
};

const LearningPanel = () => {
	const { t } = useTranslation("common");
	const canManageSettings = useCanManageTenantSettings();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const startTour = useStartTour();
	const { closePanel, conceptId, openConcept } = useLearnParam();
	const [shownConceptId, setShownConceptId] = useState(conceptId);
	if (conceptId && conceptId !== shownConceptId) setShownConceptId(conceptId);
	const { isEnabled: isAvailable } = useFeatureFlag(FeatureFlagName.LearningPath);
	const {
		data: learningPathData,
		isError,
		isLoading,
		refetch
	} = useMyLearningPathQuery(undefined, { skip: !shownConceptId || !isAvailable });
	const [pendingTour, setPendingTour] = useState<PendingTour | null>(null);

	useEffect(() => {
		if (!pendingTour) return;
		if (matchesWalkthroughRoute(pathname, pendingTour.routePath)) {
			startTour(pendingTour.tourId, pendingTour.conceptId);
			setPendingTour(null);
			return;
		}
		if (pathname !== pendingTour.fromPathname) setPendingTour(null);
	}, [pathname, pendingTour]);

	if (!isAvailable) return null;

	const learningPath = shownConceptId
		? learningPaths.findPathForConcept(learningPathData?.myLearningPath ?? [], shownConceptId)
		: undefined;

	const showTour = (concept: LearningConcept, tour: WalkthroughDefinition) => {
		if (matchesWalkthroughRoute(pathname, tour.routePath)) {
			closePanel();
			startTour(tour.id, concept.conceptId);
			return;
		}
		setPendingTour({
			conceptId: concept.conceptId,
			fromPathname: pathname,
			routePath: tour.routePath,
			tourId: tour.id
		});
		navigate(tour.routePath);
	};

	const renderBody = () => {
		if (isLoading) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<CircularProgress aria-label={t("learningPath.loading")} size={28} />
				</Box>
			);
		}
		if (isError) return <RetryableErrorState message={t("learningPath.loadError")} onRetry={() => void refetch()} />;
		if (!learningPath)
			return <BodyTextSmall sx={{ color: "text.secondary" }}>{t("learningPath.notFound")}</BodyTextSmall>;
		return (
			<Stack spacing={1}>
				{learningPath.concepts.map((concept) => (
					<LearningConceptRow
						canManageSettings={canManageSettings}
						concept={concept}
						key={concept.conceptId}
						onSelect={() => openConcept(concept.conceptId)}
						onShow={(tour) => showTour(concept, tour)}
						selected={concept.conceptId === shownConceptId}
					/>
				))}
			</Stack>
		);
	};

	return (
		<Drawer
			anchor="right"
			onClose={closePanel}
			open={Boolean(conceptId)}
			slotProps={{
				paper: { sx: { display: "flex", flexDirection: "column", width: { sm: PANEL_WIDTH, xs: "100vw" } } },
				transition: { onExited: () => setShownConceptId(null) }
			}}
			sx={{ zIndex: (theme) => theme.zIndex.modal }}
		>
			<Box
				sx={{
					alignItems: "center",
					borderBottom: 1,
					borderBottomColor: "divider",
					display: "flex",
					flexShrink: 0,
					gap: 1.5,
					pl: 2,
					pr: 1,
					py: 1
				}}
			>
				<Icon sx={{ color: "primary.main" }}>school</Icon>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<TitleText bold component="h2">
						{learningPath ? t(`learningPath.module.${learningPath.module}`) : t("learningPath.title")}
					</TitleText>
					{learningPath && (
						<CaptionText sx={{ color: "text.secondary" }}>
							{t("learningPath.progress", { completed: learningPath.completedCount, total: learningPath.totalCount })}
						</CaptionText>
					)}
				</Box>
				<Tooltip title={t("actions.close")}>
					<IconButton aria-label={t("learningPath.close")} onClick={closePanel} size="small">
						<Icon>close</Icon>
					</IconButton>
				</Tooltip>
			</Box>
			<Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>{renderBody()}</Box>
		</Drawer>
	);
};

export default LearningPanel;
